import React from 'react';
import PropTypes from 'prop-types';

import './HiddenFileValidationBanner.scss';

/**
 * Resolves a named HTML entity to its character. Uses a switch (not an object lookup) so it can't
 * be flagged as an object-injection sink.
 * @param {string} entity - the entity name without & or ;
 * @return {string|null} the decoded character, or null if unrecognized
 */
const decodeNamedEntity = (entity) => {
  switch (entity.toLowerCase()) {
    case 'amp': return '&';
    case 'lt': return '<';
    case 'gt': return '>';
    case 'quot': return '"';
    case 'apos': return '\'';
    case 'nbsp': return ' ';
    default: return null;
  }
};

/**
 * Decodes HTML entities (e.g. &quot; / &#39;) that Salesforce validation messages can contain.
 * Pure string parsing - no DOM / innerHTML - so nothing is ever executed or rendered as markup.
 * @param {string} str - raw message
 * @return {string} decoded message
 */
const decodeHtmlEntities = (str) => {
  if (typeof str !== 'string') {
    return str;
  }
  return str.replace(/&(#\d+|#x[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, entity) => {
    if (entity.charAt(0) === '#') {
      const isHex = entity.charAt(1).toLowerCase() === 'x';
      const code = isHex ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10);
      return Number.isNaN(code) ? match : String.fromCharCode(code);
    }
    const decoded = decodeNamedEntity(entity);
    return decoded === null ? match : decoded;
  });
};

/**
 * Warning banner shown under the Documents section when files that are hidden from the UI
 * (attachments and/or the email file, per the admin display settings) violate one or more
 * Salesforce validation rules. Because those rows aren't rendered, the inline error icon can't be
 * shown, so this banner surfaces the violated rule messages instead.
 * @param {object} props
 * @param {string} props.message - headline explaining what will not be saved
 * @param {string[]} props.rules - distinct violated rule messages
 * @param {string} [props.testId] - automation id
 * @return {JSX.Element|null}
 */
export const HiddenFileValidationBanner = ({ message, rules = [], testId }) => {
  if (!rules.length) {
    return null;
  }

  return (
    <div className='hiddenFileValidationBanner' data-testid={testId} role='alert'>
      <div className='hiddenFileValidationBanner-header'>
        <span className='hiddenFileValidationBanner-icon'>
          <svg viewBox='0 0 16 16' width='22' height='22' aria-hidden='true' focusable='false'>
            <path d='M8 1.5 L15.2 14.5 L0.8 14.5 Z' fill='#a86403' />
            <rect x='7.1' y='5.6' width='1.8' height='4.6' rx='0.9' fill='#fff' />
            <circle cx='8' cy='12.2' r='1' fill='#fff' />
          </svg>
        </span>
        <p className='hiddenFileValidationBanner-message'>{message}</p>
      </div>
      <ol className='hiddenFileValidationBanner-list'>
        {rules.map((rule, index) => (
          <li key={`${index}-${rule}`}>{decodeHtmlEntities(rule)}</li>
        ))}
      </ol>
    </div>
  );
};

HiddenFileValidationBanner.propTypes = {
  message: PropTypes.string.isRequired,
  rules: PropTypes.arrayOf(PropTypes.string),
  testId: PropTypes.string,
};

export default HiddenFileValidationBanner;
